import axios from "axios";
import { toast } from "react-toastify";
import semver from "semver";

import { usePrevious } from "hooks";
import { CUSTOMER_TYPES, DMSIDS, INTERVENTION_ELEMENT_TYPES, ROLES, SEASONS } from "models";

// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import packageJson from "../../package.json";

import type { DropdownItemProps } from "components";
import type { TFunction } from "i18next";
import type { Appointment, Customer, DealerLocation, Intervention, InterventionElement, User, VehicleTyreReplacement } from "models";

export const openNewTabURL = (url: string) => window.open(url, "_blank", "noopener,noreferrer");
export const clickOnId = (elementId: string) => document.getElementById(elementId)?.click();

export const LATEST_VERSION_ENDPOINT = "/latest_version.json";

export const checkForNewAppVersion = async () => {
  try {
    const { latest_version } = (await axios.get<{ latest_version?: string }>(LATEST_VERSION_ENDPOINT)).data;

    if (!latest_version || !semver.valid(latest_version)) {
      console.warn("App version check failed, unusable latest_version received:", latest_version);
      return false;
    }

    return semver.gt(latest_version, packageJson.version);
  } catch (error) {
    console.warn("App version check failed, could not read /latest_version.json", error);
    return false;
  }
};

type GetCustomerNameParams = {
  appointment: Appointment;
  customerType: CUSTOMER_TYPES;
  showTitle: boolean;
  t: TFunction;
};

export const SELECT_ALL_DROPDOWN_VALUE = -1;

export const REPORTS_COLORS = ["#278ecf", "#4bd762", "#ff402c", "#ff9416", "#d42ae8", "#535ad7", "#83bfff", "#ffca1f", "#44BA45", "#2F6F24", "#AE2DFF", "#2285D0"];
export const getErrorMessage = (error: any): string => (error.response?.data?.errors[0].length > 0 ? error.response?.data?.errors[0] : "");
export const isNumOrStr = (value: number | string | undefined) => typeof value === "number" || typeof value === "string";
export const defaultFormatter = (value: (number | string)[]) => (Array.isArray(value) && isNumOrStr(value[0]) && isNumOrStr(value[1]) ? value.join(" ~ ") : value);

export const classNames = (classes: Record<string, boolean | undefined | null | 0>): string => {
  return Object.entries(classes)
    .filter(([_, condition]) => Boolean(condition))
    .map(([className]) => className)
    .join(" ");
};

export enum PAGE_SIZE {
  PageSize10 = 10,
  PageSize20 = 20,
  PageSize25 = 25,
  PageSize50 = 50,
  PageSize100 = 100
}

export const setPreference = (key: string, value: any) => localStorage.setItem(`${key}`, JSON.stringify({ value }));

export const getPreference = <T>(key: string, defaultValue: T): T => {
  const item = localStorage.getItem(`${key}`);
  if (!item) return defaultValue;

  try {
    return JSON.parse(item).value;
  } catch (_) {
    return defaultValue;
  }
};

export const removeGenerationParam = (urlPath: string) => {
  if (!urlPath) return "";

  if (urlPath.indexOf("generation") === -1) return urlPath;

  return urlPath.replace("generation", "noversion");
};

export const copyToClipboard = async (txt: string) => {
  try {
    await navigator.clipboard.writeText(txt);
  } catch (err) {
    toast.error("Failed to copy");
    throw err;
  }
};

export const convertCamelCaseToWords = (text: string) => {
  if (!text) return "";

  const result = text
    .replace(/([A-Z]+)(?=[A-Z][a-z])|([a-z0-9])([A-Z])/g, (_, p1, p2, p3) => {
      return p1 ? p1 + " " : p2 + " " + p3;
    })
    .trim();

  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const camelCaseToSnakeCase = (value: string) => {
  if (!value.length) return "";

  return value.split("").reduce((acc, curr, idx) => {
    if (/[A-Z]/.test(curr)) return acc + (idx === 0 ? curr.toLowerCase() : `_${curr.toLowerCase()}`);
    return acc + curr;
  }, "");
};

export const getTranslatedLabel = (label: string, t: TFunction) => {
  const translationKey = `v8_${camelCaseToSnakeCase(label)}`;
  const fallback = convertCamelCaseToWords(label);
  const translation = t(translationKey, fallback);

  return translation === translationKey ? fallback : translation;
};

const CustomerFieldPrefixMap: Map<CUSTOMER_TYPES, string> = new Map([
  [CUSTOMER_TYPES.NotSet, ""],
  [CUSTOMER_TYPES.Driver, "driver_"],
  [CUSTOMER_TYPES.Owner, "owner_"],
  [CUSTOMER_TYPES.Contractor, "contractor_"]
]);

const AppointmentCustomerNameKeys = [
  "driver_title",
  "driver_firstname",
  "driver_surname",
  "owner_title",
  "owner_firstname",
  "owner_surname",
  "contractor_title",
  "contractor_firstname",
  "contractor_surname"
];

const isAppointmentProperty = (appointment: Appointment, property: string): property is keyof Appointment => property in appointment;

const getAppointmentCustomerNameProperty = (appointment: Appointment, property: string) => {
  return isAppointmentProperty(appointment, property) && AppointmentCustomerNameKeys.includes(property) && typeof appointment[property] === "string"
    ? appointment[property]
    : "";
};

export const getCustomerName = ({ appointment, customerType, showTitle = true, t }: GetCustomerNameParams) => {
  const prefix = CustomerFieldPrefixMap.get(customerType);
  if (!prefix) return "";

  const { driver_opted_out_from_importing, owner_opted_out_from_importing, contractor_opted_out_from_importing } = appointment;
  if (
    (customerType === CUSTOMER_TYPES.Contractor && contractor_opted_out_from_importing) ||
    (customerType === CUSTOMER_TYPES.Owner && owner_opted_out_from_importing) ||
    (customerType === CUSTOMER_TYPES.Driver && driver_opted_out_from_importing)
  )
    return t("v8_customer_opted_out", "Customer opted out");

  const title = getAppointmentCustomerNameProperty(appointment, prefix + "title");
  const initials = getAppointmentCustomerNameProperty(appointment, prefix + "initials");
  const firstname = getAppointmentCustomerNameProperty(appointment, prefix + "firstname");
  const surname = getAppointmentCustomerNameProperty(appointment, prefix + "surname");

  const nameParts: string[] = [];

  if (firstname || surname) {
    if (showTitle && title) nameParts.push(title);
    nameParts.push(firstname, surname);
  } else nameParts.push(initials);

  return nameParts.filter(Boolean).join(" ").trim() || appointment.company.trim() || "";
};

export enum PRINT_SIZE {
  A4 = 1,
  Label,
  KeyTag
}

export const getBrowserLanguage = (loadFromLocalStorage = true) => {
  if (loadFromLocalStorage) {
    const locale = localStorage.getItem("locale");
    if (locale && (locale === "en-GB" || locale === "nl-NL" || locale === "de-DE" || locale === "fr-FR")) {
      return locale;
    }
  }

  const queryLanguage = new URLSearchParams(window.location.search).get("lang");
  if (queryLanguage) return queryLanguage;

  let language = "";
  if (navigator.language.includes("en")) {
    language = "en-GB";
  } else if (navigator.language.includes("nl")) {
    language = "nl-NL";
  } else if (navigator.language.includes("de")) {
    language = "de-DE";
  } else if (navigator.language.includes("fr")) {
    language = "fr-FR";
  } else {
    language = "en-GB";
  }

  return language;
};

export const languages = [
  { text: "Dutch", value: "nl-NL", key: "nl" },
  { text: "French", value: "fr-FR", key: "fr" },
  { text: "English", value: "en-GB", key: "en" },
  { text: "German", value: "de-DE", key: "de" }
];

export function roundTwoDecimals(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export const getPriceInVAT = (price: number, location?: DealerLocation | null, itemVat?: number | null) => {
  if (itemVat !== null && itemVat !== undefined) return roundTwoDecimals(price * (1 + itemVat / 100));

  const vat = location?.include_vat ? 1 + Number(location.vat) / 100 : 1;
  return roundTwoDecimals(price * vat);
};

export const getPriceWithoutVat = (price: number, location?: DealerLocation | null, itemVat?: number | null) => {
  if (price === 0) return 0;
  return price / (getPriceInVAT(price, location, itemVat) / price);
};

export const getDisplayedPrice = (price: number, location?: DealerLocation, showPriceInVat?: boolean) => (showPriceInVat ? getPriceInVAT(price, location) : price);

const getVATRate = (itemVat?: number | null, location?: DealerLocation) => {
  if (itemVat !== null && itemVat !== undefined) return itemVat;
  if (location?.include_vat) return Number(location.vat);
  return 0;
};

const applyVat = (price: number, vatRate: number) => roundTwoDecimals(price * (1 + vatRate / 100));

export const getMenuPriceInVAT = (menu: InterventionElement, allElements: InterventionElement[], location?: DealerLocation) => {
  const menuTotal = (menu.price || 0) * (menu.quantity || 1);
  const menuRate = getVATRate(menu.vat, location);
  const childElements = allElements.filter(el => el.parent_dms_nr === menu.dms_nr);

  if (!childElements.some(el => el.price && el.price > 0)) return applyVat(menuTotal, menuRate);

  const childElementsSum = childElements.reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  if (roundTwoDecimals(childElementsSum) !== roundTwoDecimals(menuTotal)) return applyVat(menuTotal, menuRate);

  const groups = new Map<number, number>();

  childElements.forEach(el => {
    const rate = getVATRate(el.vat, location);
    const total = (el.price || 0) * (el.quantity || 1);
    groups.set(rate, (groups.get(rate) || 0) + total);
  });

  let result = 0;

  groups.forEach((amount, rate) => {
    result += applyVat(amount, rate);
  });

  return roundTwoDecimals(result);
};

export const getInterventionPriceWithVAT = (intervention: Intervention, location?: DealerLocation) => {
  const interventionRate = getVATRate(intervention.vat, location);
  const interventionElements = intervention.elements ?? [];

  if (!interventionElements.length) return applyVat(intervention.price, interventionRate);

  if (!interventionElements.some(el => el.price && el.price > 0)) return applyVat(intervention.price, interventionRate);

  const topLevelElements = interventionElements.filter(el => !el.parent_dms_nr);
  const topLevelElementsSum = topLevelElements
    .filter(el => el.element_type !== INTERVENTION_ELEMENT_TYPES.Menu)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  const menuSum = topLevelElements.filter(el => el.element_type === INTERVENTION_ELEMENT_TYPES.Menu).reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  if (roundTwoDecimals(topLevelElementsSum + menuSum) !== roundTwoDecimals(intervention.price)) return applyVat(intervention.price, interventionRate);

  const nonMenuGroups = new Map<number, number>();

  const result =
    topLevelElements.reduce((sum, el) => {
      if (el.element_type === INTERVENTION_ELEMENT_TYPES.Menu) return sum + getMenuPriceInVAT(el, interventionElements, location);
      const rate = getVATRate(el.vat, location);
      const total = (el.price || 0) * (el.quantity || 1);
      nonMenuGroups.set(rate, (nonMenuGroups.get(rate) || 0) + total);
      return sum;
    }, 0) + Array.from(nonMenuGroups).reduce((sum, [rate, amount]) => sum + applyVat(amount, rate), 0);

  return roundTwoDecimals(result);
};

export type VATGroup = { rate: number; amount: number };

const getMenuVATCalculation = (menu: InterventionElement, allElements: InterventionElement[], location?: DealerLocation): VATGroup[] => {
  const menuTotal = (menu.price || 0) * (menu.quantity || 1);
  const menuRate = getVATRate(menu.vat, location);
  const childElements = allElements.filter(el => el.parent_dms_nr === menu.dms_nr);

  if (!childElements.some(el => el.price && el.price > 0)) return [{ rate: menuRate, amount: menuTotal }];

  const childElementsSum = childElements.reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  if (roundTwoDecimals(childElementsSum) !== roundTwoDecimals(menuTotal)) return [{ rate: menuRate, amount: menuTotal }];

  const groups = new Map<number, number>();

  childElements.forEach(el => {
    const rate = getVATRate(el.vat, location);
    const total = (el.price || 0) * (el.quantity || 1);
    groups.set(rate, (groups.get(rate) || 0) + total);
  });

  return Array.from(groups, ([rate, amount]) => ({ rate, amount }));
};

const getInterventionVATCalculation = (intervention: Intervention, location?: DealerLocation): VATGroup[] => {
  const interventionRate = getVATRate(intervention.vat, location);
  const interventionElements = intervention.elements ?? [];

  if (!interventionElements.length) return [{ rate: interventionRate, amount: intervention.price }];

  if (!interventionElements.some(el => el.price && el.price > 0)) return [{ rate: interventionRate, amount: intervention.price }];

  const topLevelElements = interventionElements.filter(el => !el.parent_dms_nr);
  const topLevelElementsSum = topLevelElements
    .filter(el => el.element_type !== INTERVENTION_ELEMENT_TYPES.Menu)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  const menuElementsSum = topLevelElements
    .filter(el => el.element_type === INTERVENTION_ELEMENT_TYPES.Menu)
    .reduce((sum, el) => sum + (el.price || 0) * (el.quantity || 1), 0);

  if (roundTwoDecimals(topLevelElementsSum + menuElementsSum) !== roundTwoDecimals(intervention.price)) return [{ rate: interventionRate, amount: intervention.price }];

  const groups = new Map<number, number>();

  topLevelElements.forEach(el => {
    if (el.element_type === INTERVENTION_ELEMENT_TYPES.Menu)
      getMenuVATCalculation(el, interventionElements, location).forEach(g => {
        groups.set(g.rate, (groups.get(g.rate) || 0) + g.amount);
      });
    else {
      const rate = getVATRate(el.vat, location);
      const total = (el.price || 0) * (el.quantity || 1);
      groups.set(rate, (groups.get(rate) || 0) + total);
    }
  });

  return Array.from(groups, ([rate, amount]) => ({ rate, amount }));
};

export type Totals = {
  vatGroups: VATGroup[];
  totalAmount: number;
  approvedTotal: number;
};

export const getInterventionTotals = (interventions: Intervention[], location?: DealerLocation, showPriceInVAT?: boolean): Totals => {
  let totalAmount = 0;
  let approvedTotal = 0;
  const groupsMap = new Map<number, number>();

  interventions.forEach(intervention => {
    if (showPriceInVAT) {
      const priceInVAT = getInterventionPriceWithVAT(intervention, location);
      totalAmount += priceInVAT;
      if (intervention.customer_ok) approvedTotal += priceInVAT;

      getInterventionVATCalculation(intervention, location).forEach(g => {
        groupsMap.set(g.rate, (groupsMap.get(g.rate) ?? 0) + g.amount);
      });
    } else {
      totalAmount += intervention.price;
      if (intervention.customer_ok) approvedTotal += intervention.price;
    }
  });

  const vatGroups = Array.from(groupsMap, ([rate, amount]) => ({
    rate,
    amount: roundTwoDecimals((amount * rate) / 100)
  }));

  return {
    vatGroups,
    totalAmount: roundTwoDecimals(totalAmount),
    approvedTotal: roundTwoDecimals(approvedTotal)
  };
};

export const getTotals = (
  interventions: Intervention[],
  replacements: VehicleTyreReplacement[],
  location?: DealerLocation,
  showInterventionsPriceInVAT?: boolean,
  showReplacementsPriceInVAT?: boolean
): Totals => {
  let totalAmount = 0;
  let approvedTotal = 0;
  const VATRate = getVATRate(undefined, location);
  const groupsMap = new Map<number, number>();

  interventions.forEach(intervention => {
    if (showInterventionsPriceInVAT) {
      const priceInVAT = getInterventionPriceWithVAT(intervention, location);
      totalAmount += priceInVAT;
      if (intervention.customer_ok) approvedTotal += priceInVAT;

      getInterventionVATCalculation(intervention, location).forEach(g => {
        groupsMap.set(g.rate, (groupsMap.get(g.rate) ?? 0) + g.amount);
      });
    } else {
      totalAmount += intervention.price;
      if (intervention.customer_ok) approvedTotal += intervention.price;
    }
  });

  replacements.forEach(replacement => {
    if (showReplacementsPriceInVAT) {
      const priceInVAT = applyVat(replacement.price, VATRate);
      totalAmount += priceInVAT;
      groupsMap.set(VATRate, (groupsMap.get(VATRate) ?? 0) + roundTwoDecimals((totalAmount * VATRate) / 100));
    } else {
      totalAmount += replacement.price;
    }
  });

  const vatGroups = Array.from(groupsMap, ([rate, amount]) => ({
    rate,
    amount: roundTwoDecimals((amount * rate) / 100)
  }));

  return {
    vatGroups,
    totalAmount: roundTwoDecimals(totalAmount),
    approvedTotal: roundTwoDecimals(approvedTotal)
  };
};

export const getPreviewUrl = (url: string): string => {
  let modifiedUrl = url.replace("://www.", "://storage.");
  modifiedUrl = modifiedUrl.replace("/download/storage/v1/b/", "/");
  modifiedUrl = modifiedUrl.replace("/o/", "/");

  return modifiedUrl;
};

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const getCarSeasonOptions = (t: TFunction) => [
  { key: SEASONS.AllSeason, value: SEASONS.AllSeason, text: t("v8_all_season", "All season") },
  { key: SEASONS.Summer, value: SEASONS.Summer, text: t("v8_summer", "Summer") },
  { key: SEASONS.Winter, value: SEASONS.Winter, text: t("v8_winter", "Winter") }
];

export const getTyreSeasonIcon = (season?: SEASONS) => {
  switch (season) {
    case SEASONS.Winter:
      return "snowflake i-blue";
    case SEASONS.Summer:
      return "sun i-yellow";
    case SEASONS.AllSeason:
      return "sun dust i-green";
    default:
      return "";
  }
};

export const isAdmin = (role_id: number) => [ROLES.SuperAdmin, ROLES.ClaireAdmin, ROLES.SupportAdmin].includes(role_id);

export const getUserFullName = (user?: User) => (user ? `${user.first_name} ${user.last_name}`.trim() : "");

export const getUserNameInitials = (user: string | Customer | null) => {
  if (!user) return user;

  if (typeof user === "string") {
    const separator = user.includes("_") ? "_" : " ";

    const fullName = user.trim().split(separator);

    const firstName = fullName[0][0].toUpperCase() || "";
    const lastName = fullName[fullName.length - 1][0].toUpperCase() || "";

    return `${firstName}${lastName}`;
  }

  return `${user.firstname[0].toLocaleUpperCase()} ${user.surname?.[0]?.toLocaleUpperCase() ?? ""}`;
};

let timer = 0;

export const debounce = <F extends (...args: any[]) => void>(func: F, delay: number) => {
  return (...args: Parameters<F>) => {
    clearTimeout(timer);
    timer = window.setTimeout(() => func(...args), delay);
  };
};

export const validateEmail = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export const getNameFromURL = (url: string) => {
  const name = decodeURIComponent(url);
  return name.indexOf("?") > -1 ? name.slice(name.lastIndexOf("/") + 1, name.indexOf("?")) : name.slice(name.lastIndexOf("/") + 1);
};

const GCS_BASE = "https://storage.googleapis.com/";
const GCS_DOWNLOAD = "https://storage.googleapis.com/download/storage/v1/b/";

export const getGcsDownloadURL = (url: string) => {
  if (!url || !url.startsWith(GCS_BASE) || url.startsWith(GCS_DOWNLOAD)) return url;

  const { pathname, searchParams } = new URL(url);
  const [, bucket, ...objectParts] = pathname.split("/");
  if (!bucket || !objectParts.length) return url;
  searchParams.set("alt", "media");

  return `${GCS_DOWNLOAD}${bucket}/o/${objectParts.join("%2F")}?${searchParams.toString()}`;
};

export const downloadFile = async (url: string) => {
  try {
    const response = await fetch(getGcsDownloadURL(url));
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = getNameFromURL(url);

    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (_err) {
    toast.error(`Failed to download ${getNameFromURL(url)}`);
  }
};

export const downloadCloudBucketResources = async (urls: string[]) => {
  for (const url of urls) {
    await downloadFile(url);
  }
};

export const getExtensionFromURL = (url: string) => {
  const attachmentType = url.match(/\.([^?.]+)(\?.*)?$/);
  return attachmentType?.[1] || "";
};

const EXPECTED_CONTENT_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  bmp: "image/bmp",
  mp4: "video/mp4",
  webm: "video/webm"
};

const getMismatchedContentType = async (fileName: string, url: string) => {
  const expectedContentType = EXPECTED_CONTENT_TYPES[getExtensionFromURL(fileName).toLowerCase()];
  if (!url || !expectedContentType) return "";

  try {
    const response = await fetch(getGcsDownloadURL(url), { method: "HEAD" });
    if (!response.ok) return "";

    const contentType = (response.headers.get("content-type") ?? "").split(";")[0].trim().toLowerCase();
    if (!contentType || contentType === expectedContentType) return "";

    return contentType;
  } catch (_err) {
    return "";
  }
};

export const warnOnMismatchedContentType = async (fileName: string, url: string, t: TFunction) => {
  const contentType = await getMismatchedContentType(fileName, url);
  if (!contentType) return;

  toast.warning(
    t("v8_uploaded_file_may_be_damaged", "{{name}} may be damaged, it was uploaded as {{contentType}} and might not preview or download correctly", {
      name: fileName,
      contentType
    })
  );
};

const BYTES_IN_MEGABYTE = 2 ** 20;
export const MAX_UPLOAD_FILE_SIZE_BYTES = 32 * BYTES_IN_MEGABYTE;

export const getFilesWithinSizeLimit = (files: File[], t: TFunction) => {
  const { allowedFiles, rejectedNames } = files.reduce<{ allowedFiles: File[]; rejectedNames: string[] }>(
    (acc, file) => {
      if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) acc.rejectedNames.push(file.name);
      else acc.allowedFiles.push(file);

      return acc;
    },
    { allowedFiles: [], rejectedNames: [] }
  );

  if (rejectedNames.length) toast.error(t("v8_max_file_size_exceeded", "Max file size is 32MB. Not uploaded: {{names}}", { names: rejectedNames.join(", ") }));

  return allowedFiles;
};

export const isFileWithinSizeLimit = (file: File, t: TFunction) => getFilesWithinSizeLimit([file], t).length > 0;

export const getInitials = (firstName: string = "", lastName: string = "") => {
  const mechanicLastNamePart = lastName.trim().split(" ").pop() ?? "";
  return `${firstName[0] || ""}${mechanicLastNamePart[0] || ""}`.toUpperCase();
};

export const extractAllNameFields = (data: Record<string, string | number>[]) => {
  const namesCollected: string[] = [];
  data.forEach(item => Object.keys(item).forEach(name => !namesCollected.includes(name) && namesCollected.push(name)));
  return namesCollected;
};

export const translateReportForCharts = (report: string[], t: TFunction) => {
  const translatedReport: Record<string, unknown> = {};
  const skipKeys = ["name", "value", "total"];
  const prefixes = ["total", "percent", "stack_a", "stack_b"];

  for (const key in report) {
    let translatedKey = key;
    if (!skipKeys.includes(key)) {
      const [prefix, ...restKey] = key.split(".");
      if (restKey.length && prefixes.includes(prefix)) translatedKey = `${prefix}.${t(restKey.join(".")) || restKey.join(".")}`;
      else translatedKey = t(key) || key;
    }
    translatedReport[translatedKey] = report[key];
  }
  return translatedReport;
};

export const parseIntOrDefault = <T>(s: string, fallback: T): number | T => {
  const n = parseInt(s);

  return Number.isNaN(n) ? fallback : n;
};

export const useUpdateLocationPreference = (key: string, selectedLocation?: DealerLocation | null) => {
  const previousLocation = usePrevious(selectedLocation);

  if (!previousLocation && selectedLocation && !getPreference<boolean>(key, false)) setPreference(key, [selectedLocation.id]);
};

export const validatePhoneNr = (input: string) => {
  const regex = new RegExp(/^\+?[\d\s\-().]{7,20}$/);
  return regex.test(input);
};

export const convertSnakeCaseToWords = (value: string) => value.split("_").join(" ");
export const convertPascalCaseToSnakeCase = (text: string) => text.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
export const convertPascalCaseToText = (text: string) => text.replace(/([A-Z])/g, " $1").trim();

type EnumToDropdownOptionsConfig<T extends Record<string, string | number>> = {
  enumObj: T;
  t?: TFunction;
  keysToExclude?: (keyof T & string)[];
  formatLabel?: (key: string) => string;
};

export const enumToDropdownOptions = <T extends Record<string, string | number>>({
  t,
  enumObj,
  keysToExclude = [],
  formatLabel = convertPascalCaseToText
}: EnumToDropdownOptionsConfig<T>): DropdownItemProps[] =>
  Object.entries(enumObj).flatMap(([key, value]) => {
    if (typeof value !== "number" || keysToExclude.includes(key)) return [];
    const label = formatLabel(key);

    return [{ key: value, value, text: t ? t(`v8_${convertPascalCaseToSnakeCase(key)}`, label) : label }];
  });

export const getUserRoleName = (t: TFunction, roleId?: ROLES) => {
  if (roleId == null) return "";

  const key = ROLES[roleId];
  if (!key) return "";

  const snakeKey = convertPascalCaseToSnakeCase(key);
  return t(`v8_${snakeKey}`, capitalizeFirstLetter(snakeKey.replace(/_/g, " ")));
};

export const isKeyLoopLocation = (location?: DealerLocation | null) =>
  Boolean(location?.dms_id && [DMSIDS.KEYLOOP_MENUS, DMSIDS.KEYLOOP_JOBS, DMSIDS.KEYLOOP_MENUS_STAGING, DMSIDS.KEYLOOP_JOBS_STAGING].includes(location.dms_id));
export const isAutolineLocation = (location?: DealerLocation | null) =>
  Boolean(location?.dms_id && [DMSIDS.AUTOLINE, DMSIDS.AUTOLINE_OPEL_MENU, DMSIDS.AUTOLINE_EF_NEFKENS].includes(location.dms_id));
export const getFileFormData = (file: File) => {
  const extension = file.name.lastIndexOf(".") > -1 ? file.name.slice(file.name.lastIndexOf(".") + 1) : "unknown";
  const uploadFile = new File([file], file.name, { type: file.type || extension });
  const formData = new FormData();

  formData.append("file", uploadFile);

  return formData;
};

export const delay = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

// prettier-ignore
export const COUNTRY_CODES = ["AF", "AX", "AL", "DZ", "AS", "AD", "AO", "AI", "AQ", "AG", "AR", "AM", "AW", "AU", "AT", "AZ", "BS", "BH", "BD", "BB", "BY", "BE", "BZ", "BJ", "BM", "BT", "BO", "BQ", "BA", "BW", "BV", "BR", "IO", "BN", "BG", "BF", "BI", "KH", "CM", "CA", "CV", "KY", "CF", "TD", "CL", "CN", "CX", "CC", "CO", "KM", "CG", "CD", "CK", "CR", "CI", "HR", "CU", "CW", "CY", "CZ", "DK", "DJ", "DM", "DO", "EC", "EG", "SV", "GQ", "ER", "EE", "ET", "FK", "FO", "FJ", "FI", "FR", "GF", "PF", "TF", "GA", "GM", "GE", "DE", "GH", "GI", "GR", "GL", "GD", "GP", "GU", "GT", "GG", "GN", "GW", "GY", "HT", "HM", "VA", "HN", "HK", "HU", "IS", "IN", "ID", "IR", "IQ", "IE", "IM", "IL", "IT", "JM", "JP", "JE", "JO", "KZ", "KE", "KI", "KP", "KR", "KW", "KG", "LA", "LV", "LB", "LS", "LR", "LY", "LI", "LT", "LU", "MO", "MK", "MG", "MW", "MY", "MV", "ML", "MT", "MH", "MQ", "MR", "MU", "YT", "MX", "FM", "MD", "MC", "MN", "ME", "MS", "MA", "MZ", "MM", "NA", "NR", "NP", "NL", "NC", "NZ", "NI", "NE", "NG", "NU", "NF", "MP", "NO", "OM", "PK", "PW", "PS", "PA", "PG", "PY", "PE", "PH", "PN", "PL", "PT", "PR", "QA", "RE", "RO", "RU", "RW", "BL", "SH", "KN", "LC", "MF", "PM", "VC", "WS", "SM", "ST", "SA", "SN", "RS", "SC", "SL", "SG", "SX", "SK", "SI", "SB", "SO", "ZA", "GS", "SS", "ES", "LK", "SD", "SR", "SJ", "SZ", "SE", "CH", "SY", "TW", "TJ", "TZ", "TH", "TL", "TG", "TK", "TO", "TT", "TN", "TR", "TM", "TC", "TV", "UG", "UA", "AE", "GB", "US", "UM", "UY", "UZ", "VU", "VE", "VN", "VG", "VI", "WF", "EH", "YE", "ZM", "ZW", "XK"];
const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
export const countryDropdownOptions = COUNTRY_CODES.map(code => ({ value: code, key: code, text: `${code} - ${countryNames.of(code)}` }));

export const EAN_VALIDATION = /^[0-9]{13}$/;
export const isKnownManufacturer = (brandOptions: DropdownItemProps<string>[], manufacturer: string) => brandOptions.some(({ value }) => value === manufacturer);
